/**
 * Service Catalog Hooks
 * TanStack Query hooks for CDT codes and service catalog
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import type {
  DentalServiceCatalog,
  ApiResponse,
} from '../types';

// Query keys
export const catalogKeys = {
  all: ['catalog'] as const,
  lists: () => [...catalogKeys.all, 'list'] as const,
  list: (filters: CatalogFilters) => [...catalogKeys.lists(), filters] as const,
  categories: () => [...catalogKeys.all, 'categories'] as const,
};

// Types
export interface CatalogFilters {
  category?: string;
  search?: string;
  active?: boolean;
}

export interface CategoryCount {
  category: string;
  count: number;
}

// ============================================================================
// LIST SERVICES
// ============================================================================

export function useCatalogQuery(filters: CatalogFilters = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: catalogKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.category) params.set('category', filters.category);
      if (filters.search) params.set('search', filters.search);
      if (filters.active !== undefined) params.set('active', String(filters.active));

      const response = await api.get<
        ApiResponse<DentalServiceCatalog[]>
      >(`/catalog?${params.toString()}`, token || undefined);

      return response.data;
    },
    enabled: !!token,
  });
}

// ============================================================================
// GET CATEGORIES
// ============================================================================

export function useCatalogCategoriesQuery() {
  const { token } = useAuth();

  return useQuery({
    queryKey: catalogKeys.categories(),
    queryFn: async () => {
      const response = await api.get<
        ApiResponse<CategoryCount[]>
      >('/catalog/categories', token || undefined);
      return response.data;
    },
    enabled: !!token,
  });
}

// ============================================================================
// SEARCH CDT CODES
// ============================================================================

export function useSearchCDTCodes(search: string) {
  const { token } = useAuth();

  return useQuery({
    queryKey: [...catalogKeys.lists(), 'search', search],
    queryFn: async () => {
      if (!search || search.length < 2) {
        return [];
      }

      const params = new URLSearchParams();
      params.set('search', search);
      params.set('active', 'true');

      const response = await api.get<
        ApiResponse<DentalServiceCatalog[]>
      >(`/catalog?${params.toString()}`, token || undefined);

      return response.data;
    },
    enabled: !!token && search.length >= 2,
  });
}

// ============================================================================
// IMPORT DEFAULT SERVICES
// ============================================================================

export async function importDefaultServices(token: string): Promise<void> {
  await api.post('/catalog/import', {}, token);
}

// ============================================================================
// CREATE SERVICE
// ============================================================================

export async function createService(
  data: Partial<DentalServiceCatalog>,
  token: string
): Promise<DentalServiceCatalog> {
  const response = await api.post<ApiResponse<DentalServiceCatalog>>(
    '/catalog',
    data,
    token
  );
  return response.data;
}

// ============================================================================
// UPDATE SERVICE
// ============================================================================

export async function updateService(
  id: number,
  data: Partial<DentalServiceCatalog>,
  token: string
): Promise<DentalServiceCatalog> {
  const response = await api.put<ApiResponse<DentalServiceCatalog>>(
    `/catalog/${id}`,
    data,
    token
  );
  return response.data;
}

// ============================================================================
// DELETE SERVICE
// ============================================================================

export async function deleteService(id: number, token: string): Promise<void> {
  await api.delete(`/catalog/${id}`, token);
}
